/**
 * Documentation Panel Component
 *
 * A slide-out panel that shows contextual documentation for
 * EnergyPlus objects and fields.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useDocsStore } from '../store/docsStore';
import { useIDDStore } from '@/stores/iddStore';
import { DocsSearch } from './DocsSearch';
import { ObjectReferenceContent } from './ObjectReferenceContent';
import { MarkdownRenderer } from '../services/markdownRenderer';
import { cn } from '@/lib/utils';
import type { DocsPanelState } from '../types';

// Icons
function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function BookOpenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

interface DocsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  objectType?: string | undefined;
  fieldName?: string | undefined;
}

type TabType = DocsPanelState['activeTab'];

// Constants for panel width
const DOCS_PANEL_MIN_WIDTH = 320;
const DOCS_PANEL_MAX_WIDTH = 800;
const DOCS_PANEL_DEFAULT_WIDTH = 520;
const DOCS_PANEL_STORAGE_KEY = 'docs-panel-width';

export function DocsPanel({ isOpen, onClose, objectType, fieldName }: DocsPanelProps) {
  const [showSearch, setShowSearch] = useState(false);
  const [viewedObjectType, setViewedObjectType] = useState<string | null>(objectType || null);
  const [activeTab, setActiveTab] = useState<TabType>('reference');

  // Resizable width state
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const stored = localStorage.getItem(DOCS_PANEL_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= DOCS_PANEL_MIN_WIDTH && parsed <= DOCS_PANEL_MAX_WIDTH) {
        return parsed;
      }
    }
    return DOCS_PANEL_DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const lastTapTimeRef = useRef<number>(0);

  const schema = useIDDStore((state) => state.schema);
  const isSchemaLoaded = useIDDStore((state) => state.isLoaded);
  const getObjectDocumentation = useDocsStore((state) => state.getObjectDocumentation);
  const getRelatedObjects = useDocsStore((state) => state.getRelatedObjects);
  const getEngineeringDocsForObject = useDocsStore((state) => state.getEngineeringDocsForObject);
  const getInputOutputDocsForObject = useDocsStore((state) => state.getInputOutputDocsForObject);

  // Persist width to localStorage
  useEffect(() => {
    localStorage.setItem(DOCS_PANEL_STORAGE_KEY, panelWidth.toString());
  }, [panelWidth]);

  // Handle resize start
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  // Handle touch start on resize handle (with double-tap detection)
  const handleResizeTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;

    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // ms

    // Check for double-tap to reset width
    if (now - lastTapTimeRef.current < DOUBLE_TAP_DELAY) {
      // Double-tap detected - reset to default width
      setPanelWidth(DOCS_PANEL_DEFAULT_WIDTH);
      lastTapTimeRef.current = 0;
      return;
    }

    lastTapTimeRef.current = now;
    setIsResizing(true);
    startXRef.current = touch.clientX;
    startWidthRef.current = panelWidth;
  }, [panelWidth]);

  // Handle resize move (mouse and touch)
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      // For right-side panel, dragging left increases width
      const delta = startXRef.current - e.clientX;
      const newWidth = Math.min(DOCS_PANEL_MAX_WIDTH, Math.max(DOCS_PANEL_MIN_WIDTH, startWidthRef.current + delta));
      setPanelWidth(newWidth);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      const delta = startXRef.current - touch.clientX;
      const newWidth = Math.min(DOCS_PANEL_MAX_WIDTH, Math.max(DOCS_PANEL_MIN_WIDTH, startWidthRef.current + delta));
      setPanelWidth(newWidth);
    };

    const handleEnd = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isResizing]);

  // Handle double-click to reset width
  const handleResizeDoubleClick = useCallback(() => {
    setPanelWidth(DOCS_PANEL_DEFAULT_WIDTH);
  }, []);

  // Update viewed object when prop changes
  useEffect(() => {
    if (objectType) {
      setViewedObjectType(objectType);
      setShowSearch(false);
    }
  }, [objectType]);

  const docs = useMemo(() => {
    if (!viewedObjectType || !isSchemaLoaded) return null;
    return getObjectDocumentation(viewedObjectType, schema);
  }, [viewedObjectType, schema, isSchemaLoaded, getObjectDocumentation]);

  const relatedObjects = useMemo(() => {
    if (!viewedObjectType || !isSchemaLoaded) return [];
    return getRelatedObjects(viewedObjectType, schema);
  }, [viewedObjectType, schema, isSchemaLoaded, getRelatedObjects]);

  // Get engineering and input-output docs for the current object
  const engineeringDocs = useMemo(() => {
    if (!viewedObjectType) return [];
    return getEngineeringDocsForObject(viewedObjectType);
  }, [viewedObjectType, getEngineeringDocsForObject]);

  const inputOutputDocs = useMemo(() => {
    if (!viewedObjectType) return [];
    return getInputOutputDocsForObject(viewedObjectType);
  }, [viewedObjectType, getInputOutputDocsForObject]);

  // Tab configuration
  const tabs: { id: TabType; label: string; shortLabel: string; available: boolean }[] = useMemo(() => [
    { id: 'reference', label: 'Reference', shortLabel: 'Ref', available: !!docs },
    { id: 'input-output', label: 'I/O Reference', shortLabel: 'I/O', available: inputOutputDocs.length > 0 },
    { id: 'engineering', label: 'Engineering', shortLabel: 'Eng', available: engineeringDocs.length > 0 },
    { id: 'examples', label: 'Examples', shortLabel: 'Ex', available: false }, // Future feature
  ], [docs, inputOutputDocs.length, engineeringDocs.length]);

  const handleNavigate = useCallback((newObjectType: string) => {
    setViewedObjectType(newObjectType);
  }, []);

  const handleSearchSelect = useCallback((result: { objectType: string }) => {
    setViewedObjectType(result.objectType);
    setShowSearch(false);
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className="fixed inset-0 bg-black/20 z-40 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed right-0 top-0 bottom-0',
          'border-l bg-background shadow-xl z-50 flex flex-col',
          'transform transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        style={{
          width: typeof window !== 'undefined' && window.innerWidth < 640 ? '100%' : `${String(panelWidth)}px`,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="docs-panel-title"
      >
        {/* Resize handle - hidden on mobile */}
        <div
          className={cn(
            'absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10',
            'hover:bg-primary/50 active:bg-primary',
            'transition-colors duration-150 hidden sm:block touch-none',
            isResizing && 'bg-primary'
          )}
          onMouseDown={handleResizeMouseDown}
          onTouchStart={handleResizeTouchStart}
          onDoubleClick={handleResizeDoubleClick}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize documentation panel"
          aria-valuenow={panelWidth}
          aria-valuemin={DOCS_PANEL_MIN_WIDTH}
          aria-valuemax={DOCS_PANEL_MAX_WIDTH}
        />
        {/* Resize overlay to capture mouse events during resize */}
        {isResizing && (
          <div className="fixed inset-0 z-50 cursor-col-resize" aria-hidden="true" />
        )}
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4 shrink-0">
          <div className="flex items-center gap-2">
            <BookOpenIcon className="h-5 w-5 text-primary" />
            <h2 id="docs-panel-title" className="text-lg font-semibold">
              {viewedObjectType || 'Documentation'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSearch(!showSearch)}
              className={cn(
                'p-2 rounded-md transition-colors',
                showSearch
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
              title="Search documentation"
            >
              <SearchIcon className="h-4 w-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Close documentation"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Search (collapsible) */}
        {showSearch && (
          <div className="border-b p-3 shrink-0">
            <DocsSearch
              onSelect={handleSearchSelect}
              placeholder="Search objects, fields, or topics..."
              autoFocus
            />
          </div>
        )}

        {/* Tab Bar */}
        {docs && (
          <div className="border-b shrink-0">
            <div className="flex px-2">
              {tabs.filter(tab => tab.available).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'px-3 py-2 text-sm font-medium border-b-2 transition-colors',
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
                  )}
                >
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.shortLabel}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {!isSchemaLoaded ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-muted-foreground">
                <p>Loading schema...</p>
              </div>
            </div>
          ) : docs ? (
            <>
              {/* Reference Tab */}
              {activeTab === 'reference' && (
                <ObjectReferenceContent
                  objectType={viewedObjectType!}
                  memo={docs.memo}
                  fields={docs.fields}
                  isUnique={docs.isUnique}
                  isRequired={docs.isRequired}
                  highlightField={fieldName}
                />
              )}

              {/* Input-Output Reference Tab */}
              {activeTab === 'input-output' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground">Input-Output Reference</h2>
                  {inputOutputDocs.length > 0 ? (
                    inputOutputDocs.map((doc) => (
                      <div key={doc.id}>
                        <h3 className="text-lg font-medium mb-2">{doc.title}</h3>
                        <MarkdownRenderer content={doc.content} />
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground">No Input-Output reference available for this object.</p>
                  )}
                </div>
              )}

              {/* Engineering Reference Tab */}
              {activeTab === 'engineering' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground">Engineering Reference</h2>
                  {engineeringDocs.length > 0 ? (
                    engineeringDocs.map((doc) => (
                      <div key={doc.id}>
                        <h3 className="text-lg font-medium mb-2">{doc.title}</h3>
                        <MarkdownRenderer content={doc.content} />
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground">No engineering reference available for this object.</p>
                  )}
                </div>
              )}

              {/* Examples Tab (placeholder) */}
              {activeTab === 'examples' && (
                <div className="text-center text-muted-foreground py-8">
                  <p>Example IDF snippets coming soon...</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              <SearchIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Search for an object to view its documentation</p>
              <button
                onClick={() => setShowSearch(true)}
                className="mt-4 text-primary hover:underline"
              >
                Open search
              </button>
            </div>
          )}
        </div>

        {/* Related Objects - touch-friendly */}
        {docs && relatedObjects.length > 0 && (
          <div className="border-t p-3 sm:p-4 shrink-0">
            <h3 className="text-sm font-medium mb-2">Related Objects</h3>
            <div className="flex flex-wrap gap-2">
              {relatedObjects.map((related) => (
                <button
                  key={related}
                  onClick={() => handleNavigate(related)}
                  className="text-xs px-2.5 py-1.5 sm:px-2 sm:py-1 rounded bg-muted hover:bg-muted/80 text-foreground transition-colors touch-manipulation"
                >
                  {related}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Floating button to open the docs panel
 */
export function DocsFloatingButton() {
  const { isPanelOpen, openPanel, closePanel } = useDocsStore();

  return (
    <button
      onClick={() => (isPanelOpen ? closePanel() : openPanel())}
      className={cn(
        'fixed bottom-20 right-4 z-30 p-3 rounded-full shadow-lg transition-all',
        'bg-primary text-primary-foreground hover:bg-primary/90',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        isPanelOpen && 'bg-muted text-muted-foreground hover:bg-muted/80'
      )}
      title={isPanelOpen ? 'Close documentation' : 'Open documentation'}
    >
      <BookOpenIcon className="h-5 w-5" />
    </button>
  );
}
